import { useEffect, useState } from 'react';
import { Cookie, X } from 'lucide-react';
import {
  applyStoredAnalyticsConsent,
  getCookieConsent,
  saveCookieConsent,
  startOptionalAnalytics,
  stopOptionalAnalytics,
} from '../lib/analytics';

export default function CookieConsentBanner() {
  const initialConsent = getCookieConsent();

  const [consent, setConsent] = useState(initialConsent);
  const [isOpen, setIsOpen] = useState(!initialConsent);

  useEffect(() => {
    const storedConsent = applyStoredAnalyticsConsent();

    setConsent(storedConsent);
    setIsOpen(!storedConsent);

    const openCookieSettings = () => {
      setIsOpen(true);
    };

    window.addEventListener(
      'ccuk:open-cookie-settings',
      openCookieSettings,
    );

    return () => {
      window.removeEventListener(
        'ccuk:open-cookie-settings',
        openCookieSettings,
      );
    };
  }, []);

  const chooseAnalytics = (analytics) => {
    saveCookieConsent(analytics);

    if (analytics) {
      startOptionalAnalytics();
    } else {
      stopOptionalAnalytics();
    }

    setConsent({ analytics });
    setIsOpen(false);
  };

  /*
   * Once consent has been chosen there is no floating cookie control.
   * Cookie preferences are reopened from the real site footer.
   */
  if (consent && !isOpen) {
    return null;
  }

  /*
   * Existing visitor opening Cookie Settings:
   * use a compact pop-out rather than the full-width first-visit banner.
   */
  if (consent && isOpen) {
    return (
      <section
        className="
          fixed left-3 right-3 bottom-20 z-[100]
          sm:left-24 sm:right-auto sm:bottom-[5.5rem] sm:w-[390px]
          lg:left-[21rem]
          rounded-2xl border border-theme
          bg-theme-surface/95 p-4
          shadow-2xl backdrop-blur
        "
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-settings-title"
        aria-describedby="cookie-settings-description"
        data-testid="cookie-consent-banner"
      >
        <div className="flex items-start gap-3">
          <div className="
            flex h-9 w-9 flex-none items-center justify-center
            rounded-full bg-[#002FA7]/10 text-[#0055FF]
          ">
            <Cookie className="h-4.5 w-4.5" aria-hidden="true" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2
                  id="cookie-settings-title"
                  className="text-sm font-bold text-theme-text"
                >
                  Cookie settings
                </h2>

                <p className="mt-0.5 text-xs text-theme-muted">
                  Analytics are currently{' '}
                  <strong>
                    {consent.analytics ? 'accepted' : 'rejected'}
                  </strong>.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="
                  inline-flex h-9 w-9 flex-none items-center justify-center
                  rounded-full text-theme-muted
                  hover:bg-theme-secondary hover:text-theme-text
                  focus:outline-none focus:ring-2 focus:ring-[#0055FF]
                "
                aria-label="Close cookie settings"
              >
                <X className="h-4.5 w-4.5" aria-hidden="true" />
              </button>
            </div>

            <p
              id="cookie-settings-description"
              className="mt-3 text-xs leading-5 text-theme-muted"
            >
              Necessary cookies are always on. Optional Google Analytics
              and PostHog cookies only run when you accept analytics.
            </p>

            <a
              href="/cookie-policy"
              className="
                mt-2 inline-flex min-h-9 items-center
                text-xs font-semibold text-[#0055FF]
                hover:underline
              "
            >
              Read our Cookie Policy
            </a>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => chooseAnalytics(false)}
            className="
              min-h-11 rounded-xl border border-theme
              bg-theme px-3 py-2
              text-xs font-bold text-theme-text
              transition hover:bg-theme-secondary
              focus:outline-none focus:ring-2 focus:ring-[#0055FF]
            "
            data-testid="reject-analytics"
          >
            Reject analytics
          </button>

          <button
            type="button"
            onClick={() => chooseAnalytics(true)}
            className="
              min-h-11 rounded-xl bg-[#002FA7]
              px-3 py-2 text-xs font-bold text-white
              transition hover:bg-[#001F73]
              focus:outline-none focus:ring-2 focus:ring-[#0055FF]
            "
            data-testid="accept-analytics"
          >
            Accept analytics
          </button>
        </div>
      </section>
    );
  }

  /*
   * First visit / no stored decision:
   * keep the full consent banner visible so the visitor can make
   * an informed Accept / Reject choice.
   */
  return (
    <section
      className="
        fixed inset-x-0 bottom-0 z-[100]
        border-t border-theme bg-theme/95
        px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-4
        shadow-[0_-12px_40px_rgba(0,0,0,0.18)]
        backdrop-blur
        sm:px-6 sm:py-5
      "
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      data-testid="cookie-consent-banner"
    >
      <div className="
        mx-auto flex max-w-6xl flex-col gap-4
        lg:flex-row lg:items-center lg:justify-between
      ">
        <div className="flex min-w-0 gap-3">
          <div className="
            mt-0.5 hidden h-10 w-10 flex-none
            items-center justify-center rounded-full
            bg-[#002FA7]/10 text-[#0055FF]
            sm:flex
          ">
            <Cookie className="h-5 w-5" aria-hidden="true" />
          </div>

          <div className="min-w-0">
            <h2
              id="cookie-consent-title"
              className="text-base font-bold text-theme-text sm:text-lg"
            >
              Your privacy, your choice
            </h2>

            <p
              id="cookie-consent-description"
              className="mt-1 max-w-3xl text-sm leading-6 text-theme-muted"
            >
              We use optional Google Analytics and PostHog cookies to
              understand which parts of Casting Calls UK work well and
              where we can improve. They stay off unless you accept.
              Necessary cookies are always on.
            </p>

            <a
              href="/cookie-policy"
              className="
                mt-2 inline-flex min-h-11 items-center
                text-sm font-semibold text-[#0055FF]
                hover:underline
              "
            >
              Read our Cookie Policy
            </a>
          </div>
        </div>

        <div className="
          grid w-full grid-cols-1 gap-2
          sm:grid-cols-2
          lg:w-auto lg:min-w-[360px]
        ">
          <button
            type="button"
            onClick={() => chooseAnalytics(false)}
            className="
              min-h-12 rounded-xl border border-theme
              bg-theme px-5 py-3
              text-sm font-bold text-theme-text
              transition hover:bg-theme-secondary
              focus:outline-none focus:ring-2 focus:ring-[#0055FF]
              focus:ring-offset-2
            "
            data-testid="reject-analytics"
          >
            Reject analytics
          </button>

          <button
            type="button"
            onClick={() => chooseAnalytics(true)}
            className="
              min-h-12 rounded-xl bg-[#002FA7]
              px-5 py-3 text-sm font-bold text-white
              transition hover:bg-[#001F73]
              focus:outline-none focus:ring-2 focus:ring-[#0055FF]
              focus:ring-offset-2
            "
            data-testid="accept-analytics"
          >
            Accept analytics
          </button>
        </div>
      </div>
    </section>
  );
}
